import axios from 'axios'
import {
  delay
} from 'redux-saga'
import FileSaver from 'file-saver'

import {
  select,
  fork,
  put,
  all,
  call,
  takeEvery,
  cancelled,
} from 'redux-saga/effects'

import T from 'i18n-react'

import {
  jwtConfig,
  get,
  post,
  patch,
  del
} from 'helpers/RequestUtil'

import { baseURL } from 'config'

import {
  success,
  error
} from 'containers/App/Notification/actions'

import {
  FETCH_PROJECT_FILES,
  FETCH_PROJECT_FILES_START,
  FETCH_PROJECT_FILES_SUCCESS,
  FETCH_PROJECT_FILES_ERROR,
  POST_FILE_REMOVE,
  POST_FILE_REMOVE_START,
  POST_FILE_REMOVE_SUCCESS,
  POST_FILE_REMOVE_ERROR,
  PATCH_FILE_NAME,
  PATCH_FILE_NAME_START,
  PATCH_FILE_NAME_SUCCESS,
  PATCH_FILE_NAME_ERROR,
  FETCH_FILE,
  PATCH_FILE_INTERNAL,
  FETCH_FILE_FOR_PREVIEW,
  PATCH_FILE_DESCRIPTION
} from './constants'

import {
  fetchProjectFilesStart,
  fetchProjectFilesSucess,
  fetchProjectFilesError,
  postFileRemoveStart,
  postFileRemoveSuccess,
  postFileRemoveError,
  patchFileNameStart,
  patchFileNameSuccess,
  patchFileNameError,
  patchFileDescriptionStart,
  patchFileDescriptionSuccess,
  patchFileDescriptionError,
  patchFileInternalStart,
  patchFileInternalSuccess,
  patchFileInternalError,
  fetchFileStart,
  fetchFileSuccess,
  fetchFileError,
  fetchFileForPreviewStart,
  fetchFileForPreviewSuccess,
  fetchFileForPreviewError,
} from './actions'

import {
  updatePreviewLoading,
  updatePreviewFile
} from 'containers/Project/FilePreview/actions'

import {
  updateTables,
  overwriteTables,
  removeRow,
} from 'containers/Entity/actions'

import {
  fetchFileForThumbnail,
} from 'containers/Project/Thumbnail/actions'

function* fetchProjectFiles(id) {
  try {
    yield call(clearOldProjectFiles)
    yield put(fetchProjectFilesStart())
    const data = yield call(
      get,
      `/api/fulcrum/projects/${id}/files`
    )
    yield put(overwriteTables(data))
    yield put(fetchProjectFilesSucess())
  } catch(error) {
    yield put(fetchProjectFilesError(error))
  }
}

function* clearOldProjectFiles() {
  const data = {
    result: {project_files: []},
    entities: {project_files: {}}
  }
  yield put(overwriteTables(data))
}

function* fetchFile(id, name) {
  try {
    yield put(fetchFileStart())
    const { data } = yield call(
      axios.get,
     `${baseURL}/api/fulcrum/project/file_download/${id}`,
      {
        ...jwtConfig(),
        responseType: 'blob'
      }
    )
    FileSaver.saveAs(data, name)
    yield put(fetchFileSuccess())
  } catch(error) {
    yield put(fetchFileError(error))
  }
}

function* fetchFileForPreview(id, name) {
  try {
    yield put(updatePreviewLoading(true))
    yield put(fetchFileForPreviewStart())
    const { data } = yield call(
      axios.get,
     `${baseURL}/api/fulcrum/project/file_download/${id}`,
      {
        ...jwtConfig(),
        responseType: 'blob'
      }
    )
    const localFileUrl = window.URL.createObjectURL(data)
    yield put(updatePreviewFile(localFileUrl, name, id))
    yield put(fetchFileForPreviewSuccess())
    yield put(updatePreviewLoading(false))
  } catch(error) {
    yield put(fetchFileForPreviewError(error))
    yield put(updatePreviewLoading(false))
  }
}

function* postFileRemove(id) {
  try {
    yield put(postFileRemoveStart())
    yield put(removeRow('project_files', id))
    const data = yield call(
      del,
      `/api/fulcrum/project_files/${id}`
    )
    yield put(postFileRemoveSuccess())
  } catch(error) {
    yield put(postFileRemoveError(error))
  }
}

function* patchFileName(id, name) {
  try {
    yield put(patchFileNameStart())
    const data = yield call(
      patch,
      id,
      'project_files',
      { name },
      '/api/fulcrum/project_files'
    )
    yield put(updateTables(data))
    yield put(patchFileNameSuccess())
  } catch(error) {
    yield put(patchFileNameError(error))
  }
}

function* patchFileDescription(id, description) {
  try {
    yield put(patchFileDescriptionStart())
    const data = yield call(
      patch,
      id,
      'project_files',
      { description },
      '/api/fulcrum/project_files'
    )
    yield put(updateTables(data))
    yield put(patchFileDescriptionSuccess())
  } catch(error) {
    yield put(patchFileDescriptionError(error))
  }
}

function* patchFileInternal(id, internal) {
  try {
    yield put(patchFileInternalStart())
    const data = yield call(
      patch,
      id,
      'project_files',
      { internal },
      '/api/fulcrum/project_files'
    )
    yield put(updateTables(data))
    yield put(patchFileInternalSuccess())
  } catch(error) {
    yield put(patchFileInternalError(error))
  }
}

function* handlePatchFileInternal(action) {
  const { id, internal } = action
  yield call(patchFileInternal, id, !internal)
}

function* handlePatchFileName(action) {
  const { id, name } = action
  yield call(patchFileName, id, name)
}

function* handlePatchFileDescription(action) {
  const { id, description } = action
  yield call(patchFileDescription, id, description)
}

function* handlePostFileRemove(action) {
  const { id } = action
  yield call(postFileRemove, id)
  yield put(fetchFileForThumbnail())
}

function* handleFetchProjectFiles(action) {
  const { id } = action
  yield call(fetchProjectFiles, id)
  yield put(fetchFileForThumbnail())
}

function* handleFetchFile(action) {
  const { id, name } = action
  yield call(fetchFile, id, name)
}

function* handleFetchFileForPreview(action) {
  const { id, name } = action
  yield call(fetchFileForPreview, id, name)
}

function* watchFetchFile() {
  yield takeEvery(FETCH_FILE, handleFetchFile)
}

function* watchFetchFileForPreview() {
  yield takeEvery(FETCH_FILE_FOR_PREVIEW, handleFetchFileForPreview)
}

function* watchPatchFileInternal() {
  yield takeEvery(PATCH_FILE_INTERNAL, handlePatchFileInternal)
}

function* watchPatchFileName() {
  yield takeEvery(PATCH_FILE_NAME, handlePatchFileName)
}

function* watchPatchFileDescription() {
  yield takeEvery(PATCH_FILE_DESCRIPTION, handlePatchFileDescription)
}

function* watchPostFileRemove() {
  yield takeEvery(POST_FILE_REMOVE, handlePostFileRemove)
}

function* watchFetchProjectFiles() {
  yield takeEvery(FETCH_PROJECT_FILES, handleFetchProjectFiles)
}

function* loadSagas() {
  yield all([
    fork(watchPostFileRemove),
    fork(watchPatchFileName),
    fork(watchPatchFileDescription),
    fork(watchPatchFileInternal),
    fork(watchFetchProjectFiles),
    fork(watchFetchFile),
    fork(watchFetchFileForPreview)
  ])
}

export default loadSagas
